/**
 * Module builder — the Blocks toolbox's Part control for ANY selection.
 *
 * A paragraph has no part of its own: it belongs to the article above it
 * (TWEAKS item 10). So the control reads the INHERITED part of every selected
 * block, and moving a paragraph selection to another part is the header-drop
 * landing (`dropModuleBlocksOnPart`) — the same function the drag and the
 * keyboard run. Articles keep `assignModulePartMany` (decided by the caller).
 */

import type { EditorState } from '@tiptap/pm/state';
import type { ModulePart } from '../../shared/spec-modules';
import { topBlocks } from '../designer/moduleInsert';
import { paragraphPin, dropModuleBlocksOnPart } from './moduleBuild';

type Dispatch = ((tr: import('@tiptap/pm/state').Transaction) => void) | undefined;

function ownPart(meta: unknown): ModulePart | undefined {
  const raw = (meta as Record<string, unknown> | undefined)?.modulePart;
  return raw === 1 || raw === 2 || raw === 3 ? (raw as ModulePart) : undefined;
}

/** The part each selected block reads as being in (its own part for an
 *  article, its pin or the article above it for a paragraph). */
export function selectionParts(state: EditorState, ids: string[]): Array<ModulePart | undefined> {
  const want = new Set(ids);
  const out: Array<ModulePart | undefined> = [];
  let cur: ModulePart | undefined;
  for (const b of topBlocks(state)) {
    const level = b.node.attrs.level as number;
    if (level === 2) cur = ownPart(b.node.attrs.meta);
    else if (level > 2) cur = paragraphPin(b.node) ?? cur;
    if (want.has(b.id)) out.push(cur);
  }
  return out;
}

/** One shared part when every selected block agrees; `null` = mixed;
 *  `undefined` = nothing selected or no part assigned yet. */
export function selectionPart(state: EditorState, ids: string[]): ModulePart | undefined | null {
  const parts = selectionParts(state, ids);
  if (!parts.length) return undefined;
  return parts.every((p) => p === parts[0]) ? parts[0] : null;
}

/** Move a selection that is not purely articles to `part`: the item 10
 *  header-drop landing, one transaction. */
export function moveSelectionToPart(
  state: EditorState,
  dispatch: Dispatch,
  ids: string[],
  part: ModulePart
): boolean {
  return dropModuleBlocksOnPart(state, dispatch, ids, part);
}
